/**
 * NavbarUserMenu — the right-side user avatar dropdown shown when a user
 * is signed in. Extracted from `Navbar.jsx` so the shell stays small.
 *
 * Lazy-fetches the MTD credits meter so it's visible without leaving
 * the page (Phase B/C requirement).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, User, FolderHeart, MessageSquareHeart, Workflow, Coins, Users,
} from "lucide-react";
import { automation as automationApi } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function NavbarUserMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!user) { setCredits(null); return; }
    automationApi.creditsMeter()
      .then(setCredits)
      .catch(() => setCredits(null));
  }, [user]);

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-full border border-rp-border bg-rp-surface px-3 py-1.5 text-sm hover:border-rp-borderHover transition-colors"
          data-testid="user-menu-trigger"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
          ) : (
            <span className="grid h-6 w-6 place-items-center rounded-full bg-rp-accent/15 text-rp-accent text-[10px] font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </span>
          )}
          <span className="hidden sm:block text-rp-text">{user.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-rp-surface border-rp-border text-rp-text"
      >
        <DropdownMenuLabel className="text-rp-textDim font-normal text-xs">
          {user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-rp-border" />
        <DropdownMenuItem
          onClick={() => navigate("/dashboard")}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-dashboard"
        >
          <User className="mr-2 h-4 w-4" /> Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate("/saved-templates")}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-saved-templates"
        >
          <FolderHeart className="mr-2 h-4 w-4" /> Saved templates
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate("/my-workflows")}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-my-workflows"
        >
          <Workflow className="mr-2 h-4 w-4" /> My workflows
        </DropdownMenuItem>
        {credits && (
          <DropdownMenuItem
            onClick={() => navigate("/my-workflows")}
            className="cursor-pointer focus:bg-rp-surface2"
            data-testid="menu-credits-meter"
          >
            <Coins className="mr-2 h-4 w-4 text-rp-accent" />
            <div className="flex-1 flex items-center justify-between gap-3">
              <span className="text-rp-textDim">MTD credits</span>
              <span className="font-mono text-rp-text" data-testid="menu-credits-mtd">
                {credits.month_to_date_credits}
                {credits.metered && credits.credits_balance != null && (
                  <span className="text-rp-textFaint"> / {credits.credits_balance}</span>
                )}
              </span>
            </div>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => navigate("/team")}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-team"
        >
          <Users className="mr-2 h-4 w-4" /> My team
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate("/my-feedback")}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-my-feedback"
        >
          <MessageSquareHeart className="mr-2 h-4 w-4" /> My feedback
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onLogout}
          className="cursor-pointer focus:bg-rp-surface2"
          data-testid="menu-logout"
        >
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
